// Custom hook for handling token/configuration generation
import { useState, useCallback, useRef } from 'react';
import {
    useGenerateApplianceTokenMutation,
    useLazyGetApplianceTokenStatusQuery,
    useGenerateApplianceConfigurationMutation,
    useLazyGetApplianceConfigurationStatusQuery,
} from '../../../../api';
import { usesConfigurationData } from '../utils/applianceDownloadHelpers';
import { CLOUD_TYPE, isVSphere } from '../../../../constants/cloudTypes';

export const useTokenGeneration = () => {
    const [generating, setGenerating] = useState(false);
    const [generatedToken, setGeneratedToken] = useState(''); // For display in UI (could be token or configuration_data)
    const [actualToken, setActualToken] = useState(''); // The actual token from response.token for API payload
    const [applianceId, setApplianceId] = useState(null);
    const pollingIntervalRef = useRef(null);

    const [generateToken] = useGenerateApplianceTokenMutation();
    const [getTokenStatus] = useLazyGetApplianceTokenStatusQuery();
    const [generateConfiguration] = useGenerateApplianceConfigurationMutation();
    const [getConfigurationStatus] = useLazyGetApplianceConfigurationStatusQuery();

    const clearPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    const pollTokenStatus = useCallback(
        async (id, cloudType) => {
            const isConfigData = usesConfigurationData(cloudType);
            const statusQuery = isConfigData ? getConfigurationStatus : getTokenStatus;

            try {
                const response = await statusQuery(id).unwrap();

                if (response.state === 'success') {
                    clearPolling();
                    setGenerating(false);
                    setActualToken(response.token);

                    const displayToken = isConfigData ? response.configuration_data : response.token;
                    setGeneratedToken(displayToken);

                    if (response.token_content?.appliance_id) {
                        setApplianceId(response.token_content.appliance_id);
                    }

                    return {
                        success: true,
                        token: response.token, // Actual token for API payload
                        displayToken: displayToken, // Token/config for display in UI
                        applianceId: response.token_content?.appliance_id,
                    };
                } else if (response.state === 'error') {
                    clearPolling();
                    setGenerating(false);
                    return { success: false, error: response.error || 'Token generation failed' };
                }

                // Still processing, continue polling
                return { success: false, pending: true };
            } catch (error) {
                clearPolling();
                setGenerating(false);
                return { success: false, error: error.message || 'Failed to check token status' };
            }
        },
        [clearPolling, getTokenStatus, getConfigurationStatus],
    );

    const generateApplianceToken = useCallback(
        async (cloudType) => {
            if (generating) {
                return { success: false, error: 'Token generation already in progress' };
            }

            setGenerating(true);
            setGeneratedToken('');
            setApplianceId(null);

            try {
                const isConfigData = usesConfigurationData(cloudType);
                const mutationFn = isConfigData ? generateConfiguration : generateToken;

                // Map cloud type for API
                let apiCloudType = cloudType;
                if (isVSphere(cloudType)) {
                    apiCloudType = 'vsphere';
                }

                // Start generation
                const response = await mutationFn(apiCloudType).unwrap();
                const generationId = response.id;

                // Start polling for status
                return new Promise((resolve) => {
                    pollingIntervalRef.current = setInterval(async () => {
                        const result = await pollTokenStatus(generationId, cloudType);

                        if (result.success || (result.error && !result.pending)) {
                            resolve(result);
                        }
                    }, 2000);
                });
            } catch (error) {
                setGenerating(false);
                return {
                    success: false,
                    error: error.data?.message || error.message || 'Failed to generate token',
                };
            }
        },
        [generating, generateToken, generateConfiguration, pollTokenStatus],
    );

    const resetTokenGeneration = useCallback(() => {
        clearPolling();
        setGenerating(false);
        setGeneratedToken('');
        setActualToken('');
        setApplianceId(null);
    }, [clearPolling]);

    const copyTokenToClipboard = useCallback(async () => {
        if (!generatedToken) {
            return { success: false, error: 'No token to copy' };
        }

        try {
            await navigator.clipboard.writeText(generatedToken);
            return { success: true };
        } catch (error) {
            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea');
                textArea.value = generatedToken;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
                return { success: true };
            } catch (fallbackError) {
                return { success: false, error: 'Failed to copy to clipboard' };
            }
        }
    }, [generatedToken]);

    return {
        generating,
        generatedToken, // For display in UI
        actualToken, // For API payload
        applianceId,
        generateApplianceToken,
        resetTokenGeneration,
        copyTokenToClipboard,
    };
};
